import * as THREE from 'three';

/** Unit/fallback trees have no render consumers while hidden. Refresh fully on reveal. */
export class VisibleUnitGroup extends THREE.Group {
  override updateMatrixWorld(force?: boolean) {
    if (this.visible) super.updateMatrixWorld(force);
  }
}

/** `Object3D.updateMatrixWorld` that leaves hidden children for when they show. */
function visibleTransforms(this: THREE.Object3D, force?: boolean) {
  if (this.matrixAutoUpdate) this.updateMatrix();
  if (this.matrixWorldNeedsUpdate || force) {
    if (this.matrixWorldAutoUpdate) {
      if (this.parent === null) this.matrixWorld.copy(this.matrix);
      else this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix);
    }
    this.matrixWorldNeedsUpdate = false;
    force = true;
  }
  const children = this.children;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    // A hidden child recomposes once it shows, whether or not its parent moves that frame.
    if (child.visible) child.updateMatrixWorld(force); else child.matrixWorldNeedsUpdate = true;
  }
}
/**
 * A figure holds every prop it might carry and shows only some, so about half its nodes are hidden at any time; the renderer's world-matrix
 * walk (and a pose's own) then skips them. A hidden node's world matrix is stale until it shows: read it through `updateWorldMatrix`.
 */
export function skipHiddenTransforms(root: THREE.Object3D) {
  root.traverse(object => { if (object.updateMatrixWorld === THREE.Object3D.prototype.updateMatrixWorld) object.updateMatrixWorld = visibleTransforms; });
}

const movingTransforms = new WeakSet<THREE.Object3D>();
/** Register CPU transform animation before freezing a completed scenery tree. */
export function animateTransform<T extends THREE.Object3D>(object: T): T {
  movingTransforms.add(object);
  return object;
}

/** Only for completed scenery attached to a stationary scene; never for character rigs. */
export function freezeSceneryTransforms(root: THREE.Object3D) {
  root.updateWorldMatrix(true, true);
  function freeze(object: THREE.Object3D, movingParent: boolean) {
    const moving = movingTransforms.has(object);
    object.matrixAutoUpdate = moving;
    object.matrixWorldAutoUpdate = moving || movingParent;
    for (const child of object.children) freeze(child, moving || movingParent);
  }
  freeze(root, false);
}
